/**
 * Modern Liquid Glass Header Component
 * Premium Apple-style header with glassmorphism
 */

import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { cn } from '../../lib/cn';
import type {
  BillingPlatform,
  BillingProvider,
  PremiumSource,
  SubscriptionManagementSurface,
  User,
  UserType,
} from '../../types';
import {
  RATE_LIMIT_PRESETS,
  type RateLimitPresetKey,
} from '../../constants/rateLimitPresets';
import {
  fetchMonthlyRateLimitStatus,
  fetchRateLimitStatus,
  type MonthlyRateLimitFeatureStatus,
  type RateLimitFeatureStatus,
} from '../../services/limits';
import { getUserVehicles, getUserConversations } from '../../services/firestore';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { CreditBadge } from '../credits/CreditBadge';
import { resolvePremiumBonusRemainingInfo } from '../../utils/premiumRemaining';

type LimitStatus = 'active' | 'exceeded' | 'locked';

interface LimitItem {
  key: RateLimitPresetKey;
  label: string;
  displayValue: string;
  secondary?: string;
  status: LimitStatus;
}

type RemoteLimitFeature = RateLimitFeatureStatus | MonthlyRateLimitFeatureStatus;

const FEATURE_KEYS: RateLimitPresetKey[] = [
  'chat',
  'conversation',
  'vehicle',
  'grounding',
  'multimodal:image',
  'multimodal:audio',
  'multimodal:video',
  'refinePrice',
];

const SANAYI_FEATURE_KEYS: RateLimitPresetKey[] = [
  'sanayiRaporu:session',
  'sanayiRaporu:bilgiToplama',
  'sanayiRaporu:ozet',
  'sanayiRaporu:raporOlustur',
  'sanayiRaporu:ustaArama',
  'sanayiRaporu:media:image',
  'sanayiRaporu:media:audio',
  'sanayiRaporu:media:video',
];

const PLAN_BADGES: Record<UserType, { label: string; className: string }> = {
  anonymous: {
    label: 'Misafir kullanıcı',
    className: 'bg-glass-100/80 text-glass-600 border border-white/50',
  },
  registered: {
    label: 'Standart kullanıcı',
    className: 'bg-accent-primary/10 text-accent-primary border border-accent-primary/20',
  },
  premium: {
    label: 'Premium kullanıcı',
    className: 'bg-amber-100 text-amber-700 border border-amber-300',
  },
};

const HEADER_TAGLINES = [
  'Aracına değer verenler için',
  'Sanayi Devrimi Cebinde',
];

const DOT_BACKGROUND_STYLE: React.CSSProperties = {
  backgroundImage:
    'radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.3) 1px, transparent 0)',
  backgroundSize: '14px 14px',
};

function getHoursUntilNextUTCReset(): number {
  const now = new Date();
  const nextReset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  return Math.max(0, (nextReset.getTime() - now.getTime()) / (1000 * 60 * 60));
}

function formatResetCountdown(): string {
  const diffHours = getHoursUntilNextUTCReset();
  const totalMinutes = Math.max(0, Math.round(diffHours * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  return `${formatted} saat sonra sıfırlanır`;
}

function parseLimitValue(limit?: string): { amount: number | null; suffix?: string } {
  if (!limit) return { amount: null };
  const [valuePart = '', suffixPart] = limit.split('/');
  const numericMatch = valuePart.match(/\d+/);
  const inlineSuffix = valuePart.replace(/\d+/g, '').trim();

  if (!numericMatch) {
    return { amount: null, suffix: suffixPart?.trim() || inlineSuffix || valuePart.trim() };
  }

  return {
    amount: Number.parseInt(numericMatch[0] ?? '0', 10),
    suffix: suffixPart?.trim() || inlineSuffix || undefined,
  };
}

function getAnonymousUsage(key: RateLimitPresetKey, limits?: User['limits']): number | undefined {
  if (!limits) return undefined;
  switch (key) {
    case 'chat':
      return limits.usedMessages ?? 0;
    case 'conversation':
      return limits.usedConversations ?? 0;
    case 'vehicle':
      return limits.usedVehicles ?? 0;
    default:
      return undefined;
  }
}

function buildLimitItems(userType: UserType, userLimits?: User['limits'], keys: RateLimitPresetKey[] = FEATURE_KEYS): LimitItem[] {
  return keys.map((key) => {
    const preset = RATE_LIMIT_PRESETS[key];
    const limitString = preset?.limits?.[userType];
    const { amount } = parseLimitValue(limitString);
    const anonymousUsage =
      userType === 'anonymous' ? getAnonymousUsage(key, userLimits) : undefined;
    const usedCount =
      typeof anonymousUsage === 'number' && amount && amount > 0
        ? Math.min(anonymousUsage, amount)
        : 0;

    let status: LimitStatus = 'active';
    let displayValue = limitString || '-';

    if (amount === null) {
      displayValue = limitString || '-';
    } else if (amount === 0) {
      status = 'locked';
      displayValue = 'Premium gerekli';
    } else {
      displayValue = `${usedCount}/${amount}`;
      if (usedCount >= amount && amount > 0) {
        status = 'exceeded';
      }
    }

    return {
      key,
      label: preset?.label || key,
      displayValue,
      secondary: undefined,
      status,
    };
  });
}

function mergeRemoteLimitData(
  base: LimitItem[],
  remote?: RemoteLimitFeature[] | null,
  actualVehicleCount?: number,
  actualConversationCount?: number,
  actualSanayiSessionCount?: number
): LimitItem[] {
  if (!remote || remote.length === 0) {
    return base;
  }

  const remoteMap = new Map(remote.map((feature) => [feature.key, feature]));

  return base.map((item) => {
    const remoteFeature = remoteMap.get(item.key);
    if (!remoteFeature) {
      return item;
    }

    if (remoteFeature.limit <= 0) {
      return {
        ...item,
        displayValue: remoteFeature.requiresPremium ? 'Premium gerekli' : item.displayValue,
        status: remoteFeature.requiresPremium ? 'locked' : item.status,
      };
    }

    // Vehicle, conversation ve sanayi session için gerçek sayıları kullan
    let used: number;
    if (item.key === 'vehicle' && actualVehicleCount !== undefined) {
      used = actualVehicleCount;
    } else if (item.key === 'conversation' && actualConversationCount !== undefined) {
      used = actualConversationCount;
    } else if (item.key === 'sanayiRaporu:session' && actualSanayiSessionCount !== undefined) {
      used = actualSanayiSessionCount;
    } else {
      used = Math.max(0, Math.min(remoteFeature.used, remoteFeature.limit));
    }

    const status: LimitStatus = remoteFeature.requiresPremium
      ? 'locked'
      : used >= remoteFeature.limit
        ? 'exceeded'
        : 'active';

    return {
      ...item,
      displayValue: `${used}/${remoteFeature.limit}`,
      status,
    };
  });
}

interface HeaderProps {
  userId?: string;
  userName?: string;
  userPhoto?: string;
  isAnonymous?: boolean;
  userType?: UserType;
  userLimits?: User['limits'];
  billingProvider?: BillingProvider;
  premiumPlatform?: BillingPlatform;
  dodoCustomerId?: string;
  premiumBonus?: User['premiumBonus'];
  storeSubscription?: User['storeSubscription'];
  onSignOut?: () => void;
  onSignIn?: () => void;
}

function resolvePremiumSourceForHeader({
  userType,
  premiumBonus,
  billingProvider,
  premiumPlatform,
  dodoCustomerId,
  storeSubscription,
}: {
  userType: UserType;
  premiumBonus?: User['premiumBonus'];
  billingProvider?: BillingProvider;
  premiumPlatform?: BillingPlatform;
  dodoCustomerId?: string;
  storeSubscription?: User['storeSubscription'];
}): PremiumSource {
  const effectiveBillingProvider = billingProvider || storeSubscription?.provider;
  const effectivePremiumPlatform = premiumPlatform || storeSubscription?.platform;

  if (
    userType === 'premium' &&
    (
      effectiveBillingProvider === 'dodo' ||
      effectiveBillingProvider === 'adapty' ||
      effectiveBillingProvider === 'apple' ||
      effectiveBillingProvider === 'google' ||
      Boolean(dodoCustomerId) ||
      effectivePremiumPlatform === 'ios' ||
      effectivePremiumPlatform === 'android'
    )
  ) {
    return 'subscription';
  }

  if (premiumBonus?.active) {
    return 'credit_purchase_bonus';
  }

  if (userType === 'premium') {
    return 'legacy';
  }

  return 'none';
}

function resolveManagementSurfaceForHeader({
  billingProvider,
  premiumPlatform,
  dodoCustomerId,
  storeSubscription,
}: {
  billingProvider?: BillingProvider;
  premiumPlatform?: BillingPlatform;
  dodoCustomerId?: string;
  storeSubscription?: User['storeSubscription'];
}): SubscriptionManagementSurface {
  const effectiveBillingProvider = billingProvider || storeSubscription?.provider;
  const effectivePremiumPlatform = premiumPlatform || storeSubscription?.platform;

  if ((effectiveBillingProvider === 'adapty' || effectiveBillingProvider === 'apple') && effectivePremiumPlatform === 'ios') {
    return 'app_store';
  }

  if ((effectiveBillingProvider === 'adapty' || effectiveBillingProvider === 'google') && effectivePremiumPlatform === 'android') {
    return 'play_store';
  }

  if ((effectiveBillingProvider === 'dodo' || dodoCustomerId) && dodoCustomerId) {
    return 'dodo_portal';
  }

  return 'none';
}

export function Header({
  userId,
  userName,
  userPhoto,
  isAnonymous,
  userType,
  userLimits,
  billingProvider,
  premiumPlatform,
  dodoCustomerId,
  premiumBonus,
  storeSubscription,
  onSignOut,
  onSignIn,
}: HeaderProps) {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [resetCountdown, setResetCountdown] = React.useState(formatResetCountdown);
  const [remoteLimitFeatures, setRemoteLimitFeatures] = React.useState<RateLimitFeatureStatus[] | null>(null);
  const [remoteMonthlyLimitFeatures, setRemoteMonthlyLimitFeatures] = React.useState<MonthlyRateLimitFeatureStatus[] | null>(null);
  const [limitsLoading, setLimitsLoading] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [vehicleCount, setVehicleCount] = React.useState<number>(0);
  const [conversationCount, setConversationCount] = React.useState<number>(0);
  const [activeSanayiSessionCount, setActiveSanayiSessionCount] = React.useState<number>(0);
  const [showMonthlyOthers, setShowMonthlyOthers] = React.useState(false);
  const [showDailyOthers, setShowDailyOthers] = React.useState(false);
  const { ozellikler } = useSystemSettings();
  const [taglineIndex, setTaglineIndex] = React.useState(0);

  const resolvedUserType: UserType =
    userType || (isAnonymous ? 'anonymous' : 'registered');
  const planBadge =
    PLAN_BADGES[resolvedUserType] || {
      label: 'Kullanıcı',
      className: 'bg-glass-100/80 text-glass-600 border border-white/40',
    };
  const tierLabel = planBadge.label;
  const premiumSource = React.useMemo(
    () =>
      resolvePremiumSourceForHeader({
        userType: resolvedUserType,
        premiumBonus,
        billingProvider,
        premiumPlatform,
        dodoCustomerId,
        storeSubscription,
      }),
    [billingProvider, dodoCustomerId, premiumBonus, premiumPlatform, resolvedUserType, storeSubscription]
  );
  const managementSurface = React.useMemo(
    () =>
      resolveManagementSurfaceForHeader({
        billingProvider,
        premiumPlatform,
        dodoCustomerId,
        storeSubscription,
      }),
    [billingProvider, dodoCustomerId, premiumPlatform, storeSubscription]
  );
  const premiumBonusRemaining = React.useMemo(
    () => resolvePremiumBonusRemainingInfo(premiumBonus),
    [premiumBonus]
  );

  const fallbackLimitItems = React.useMemo(
    () => buildLimitItems(resolvedUserType, userLimits),
    [resolvedUserType, userLimits]
  );
  const limitItems = React.useMemo(
    () => mergeRemoteLimitData(fallbackLimitItems, remoteLimitFeatures, vehicleCount, conversationCount),
    [fallbackLimitItems, remoteLimitFeatures, vehicleCount, conversationCount]
  );
  const monthlyLimitItems = React.useMemo(
    () => mergeRemoteLimitData(fallbackLimitItems, remoteMonthlyLimitFeatures, vehicleCount, conversationCount),
    [fallbackLimitItems, remoteMonthlyLimitFeatures, vehicleCount, conversationCount]
  );

  // Sanayi raporu limitleri
  const sanayiFallbackItems = React.useMemo(
    () => buildLimitItems(resolvedUserType, userLimits, SANAYI_FEATURE_KEYS),
    [resolvedUserType, userLimits]
  );
  const sanayiLimitItems = React.useMemo(
    () => mergeRemoteLimitData(sanayiFallbackItems, remoteLimitFeatures, undefined, undefined, activeSanayiSessionCount),
    [sanayiFallbackItems, remoteLimitFeatures, activeSanayiSessionCount]
  );
  const sanayiMonthlyLimitItems = React.useMemo(
    () => mergeRemoteLimitData(sanayiFallbackItems, remoteMonthlyLimitFeatures, undefined, undefined, activeSanayiSessionCount),
    [sanayiFallbackItems, remoteMonthlyLimitFeatures, activeSanayiSessionCount]
  );

  // Toplam limitleri (vehicle, conversation) ve günlük limitleri ayır
  const totalLimits = React.useMemo(
    () => limitItems.filter((item) => item.key === 'vehicle' || item.key === 'conversation'),
    [limitItems]
  );
  const monthlyUsageLimits = React.useMemo(
    () => monthlyLimitItems.filter((item) => item.key !== 'vehicle' && item.key !== 'conversation'),
    [monthlyLimitItems]
  );
  const dailyLimits = React.useMemo(
    () => limitItems.filter((item) => item.key !== 'vehicle' && item.key !== 'conversation'),
    [limitItems]
  );
  const monthlyPrimary = React.useMemo(
    () => monthlyUsageLimits.filter((item) => item.key === 'chat'),
    [monthlyUsageLimits]
  );
  const monthlySecondary = React.useMemo(
    () => monthlyUsageLimits.filter((item) => item.key !== 'chat'),
    [monthlyUsageLimits]
  );
  const monthlyOthersCount = React.useMemo(
    () => monthlySecondary.length + totalLimits.length + sanayiMonthlyLimitItems.length,
    [monthlySecondary, totalLimits, sanayiMonthlyLimitItems]
  );
  const dailyPrimary = React.useMemo(
    () => dailyLimits.filter((item) => item.key === 'chat'),
    [dailyLimits]
  );
  const dailySecondary = React.useMemo(
    () => dailyLimits.filter((item) => item.key !== 'chat'),
    [dailyLimits]
  );
  const dailyOthersCount = React.useMemo(
    () => dailySecondary.length + totalLimits.length + sanayiLimitItems.length,
    [dailySecondary, totalLimits, sanayiLimitItems]
  );

  const loadRateLimits = React.useCallback(
    async (tier: UserType) => {
      setLimitsLoading(true);
      console.log('[Header] userType from profile:', tier);
      try {
        const [status, monthlyStatus] = await Promise.all([
          fetchRateLimitStatus(tier),
          fetchMonthlyRateLimitStatus(tier),
        ]);
        setRemoteLimitFeatures(status.features);
        setRemoteMonthlyLimitFeatures(monthlyStatus.features);
      } catch (error) {
        console.error('[Header] getRateLimitStatus failed', error);
        setRemoteLimitFeatures(null);
        setRemoteMonthlyLimitFeatures(null);
      } finally {
        setLimitsLoading(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (!userType) {
      setRemoteLimitFeatures(null);
      setRemoteMonthlyLimitFeatures(null);
      return;
    }
    loadRateLimits(resolvedUserType);
  }, [userType, resolvedUserType, loadRateLimits]);

  React.useEffect(() => {
    if (isMenuOpen && userType) {
      loadRateLimits(resolvedUserType);
    }
  }, [isMenuOpen, userType, resolvedUserType, loadRateLimits]);

  // Araç ve sohbet sayısını çek
  React.useEffect(() => {
    if (!userId) return;

    Promise.all([
      getUserVehicles(userId),
      getUserConversations(userId)
    ]).then(([vehicles, conversations]) => {
      setVehicleCount(vehicles.length);
      // Filter out Sanayi Raporu from the count
      const activeNormal = conversations.filter(c => c.category !== 'sanayi-raporu');
      setConversationCount(activeNormal.length);
      // Count active sanayi raporu sessions (rapor_tamamlandi hariç)
      const activeSanayi = conversations.filter(c => {
        if (c.category !== 'sanayi-raporu') return false;
        const raporDurum = (c as any).sanayiRaporuMetadata?.raporDurum;
        return raporDurum !== 'rapor_tamamlandi';
      });
      setActiveSanayiSessionCount(activeSanayi.length);
    }).catch(err => {
      console.error('Failed to load user data:', err);
    });
  }, [userId, isMenuOpen]);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setResetCountdown(formatResetCountdown());
    }, 60000);
    return () => window.clearInterval(id);
  }, []);

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setTaglineIndex((prev) => (prev + 1) % HEADER_TAGLINES.length);
    }, 3000);
    return () => window.clearInterval(id);
  }, []);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      if (premiumSource === 'credit_purchase_bonus') {
        alert('Bu premium, kredi satın alımı bonusu olarak tanımlandı. Abonelik yönetimi gerekmez.');
        return;
      }

      if (premiumSource !== 'subscription') {
        alert('Bu hesap için yönetilebilecek bir abonelik bulunmuyor.');
        return;
      }

      if (managementSurface === 'app_store') {
        alert('Bu abonelik iPhone/iPad üzerinden satın alındı. App Store aboneliklerinden yönetin.');
        return;
      }

      if (managementSurface === 'play_store') {
        alert('Bu abonelik Google Play üzerinden satın alındı. Play Store aboneliklerinden yönetin.');
        return;
      }

      if (managementSurface === 'dodo_portal') {
        const getPortal = httpsCallable(functions, 'getCustomerPortal');
        const result = await getPortal({});
        const { portalUrl } = result.data as { portalUrl: string };
        window.location.href = portalUrl;
        return;
      }

      alert('Bu hesap için yönetilebilecek bir abonelik bulunmuyor.');
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      alert('Abonelik yönetimi açılamadı. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white/70 backdrop-blur-xl shadow-glass-sm border-b border-glass-200/50'
          : 'bg-white/50 backdrop-blur-md'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Logo */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              {/* Logo with gradient */}
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-accent-primary to-accent-secondary rounded-lg blur opacity-30" />
                <div className="relative w-10 h-10 rounded-lg shadow-glass-sm overflow-hidden bg-white flex items-center justify-center">
                  <img
                    src="/Arıza.webp"
                    alt="ArızaRehberim"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Brand */}
              <div>
                <h1 className="text-lg font-semibold bg-gradient-to-r from-glass-900 via-glass-800 to-glass-700 bg-clip-text text-transparent">
                  ArızaRehberim
                </h1>
                <p key={taglineIndex} className="text-xs text-glass-500 animate-fade-in">{HEADER_TAGLINES[taglineIndex]}</p>
              </div>
            </Link>
          </div>

          {/* Right side - User info or Sign In */}
          <div className="flex items-center gap-3">
            {userName ? (
              <div className="flex items-center gap-2">
                <DropdownMenu.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-3 rounded-2xl border border-transparent bg-white/45 px-2 py-1.5 transition-all duration-200 hover:border-glass-200/80 hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40"
                      aria-label="Günlük limitleri göster"
                    >
                      <div className="relative">
                        {userPhoto ? (
                          <img
                            src={userPhoto}
                            alt={userName}
                            className="w-10 h-10 rounded-full border-2 border-white shadow-glass-sm ring-2 ring-glass-200/50"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center text-white font-semibold shadow-glass-sm ring-2 ring-glass-200/50">
                            {(userName.charAt(0) || '?').toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="hidden sm:flex flex-col text-left">
                        <p className="text-sm font-medium text-glass-900">{userName}</p>
                        <span
                          className={cn(
                            'mt-0.5 inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-medium text-center',
                            planBadge.className
                          )}
                        >
                          {tierLabel}
                        </span>
                      </div>

                      <ChevronDown className="w-4 h-4 text-glass-500" />
                    </button>
                  </DropdownMenu.Trigger>

                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      forceMount
                      align="end"
                      sideOffset={14}
                      className={cn(
                        'z-50 dropdown-animate',
                        'relative w-[17rem] sm:w-[20rem] max-w-[88vw] max-h-[80vh] overflow-y-auto rounded-[20px] border border-white/30 bg-white/35 p-3 shadow-[0_12px_26px_rgba(15,23,42,0.2)] backdrop-blur-2xl',
                        'pointer-events-none data-[state=open]:pointer-events-auto'
                      )}
                    >
                      <div
                        className="absolute inset-0 opacity-55 pointer-events-none"
                        style={DOT_BACKGROUND_STYLE}
                      />

                      <div className="relative flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-glass-500">
                              Limitler
                            </p>
                          </div>
                          <span
                            className={cn(
                              'inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-[10px] font-medium text-center shadow-inner',
                              planBadge.className
                            )}
                          >
                            {tierLabel}
                          </span>
                        </div>

                        {premiumBonusRemaining && (
                          <div className="rounded-xl border border-amber-300/60 bg-amber-50/85 px-3 py-2 shadow-inner">
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                                  Hediye Premium
                                </p>
                                <p className="mt-0.5 text-[11px] font-medium text-amber-900">
                                  Sanayi Raporu hediyesi
                                </p>
                              </div>
                              <span className="shrink-0 rounded-full border border-amber-300/70 bg-white/70 px-2.5 py-1 text-[11px] font-bold text-amber-800">
                                {premiumBonusRemaining.shortLabel}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Sanayi Raporu Limitleri */}
                        {sanayiLimitItems.length > 0 && (
                          <div className="border-t border-white/40 pt-2 mt-1">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-glass-500 mb-1.5">
                              Sanayi Raporu
                            </p>
                          </div>
                        )}
                        {resolvedUserType !== 'anonymous' && userId && (
                          <div className="mb-1 flex flex-col gap-1">
                            <CreditBadge
                              userId={userId}
                              compact
                              forceLabel
                              label="Rapor Hakkı"
                              showReservation={false}
                              className="w-full justify-between border border-emerald-200/70 bg-emerald-50/80 shadow-inner"
                            />
                            <DropdownMenu.Item asChild>
                              <button
                                onClick={() => navigate('/app/sanayi-raporu/paketler')}
                                className="w-full rounded-lg border border-emerald-300/50 bg-emerald-50/70 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100/80 transition-colors text-left"
                              >
                                + Rapor Hakkı Al →
                              </button>
                            </DropdownMenu.Item>
                          </div>
                        )}

                        {/* Aylık Başlık */}
                        <div className="border-t border-white/40 pt-2 mt-1">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-glass-500 mb-1.5">
                            Aylık Limit
                          </p>
                        </div>

                        {/* Aylık Limitler */}
                        <div className="grid grid-cols-1 gap-1.5">
                          {monthlyPrimary.map((item) => (
                            <div
                              key={item.key}
                              className={cn(
                                'rounded-xl border border-white/40 bg-white/60 px-2.5 py-2 shadow-inner backdrop-blur-sm flex items-center justify-between gap-2',
                                item.status === 'locked' && 'opacity-70'
                              )}
                            >
                              <span className="text-[12px] font-medium text-glass-900">
                                {item.key === 'chat' ? 'Çırak Mesajı' : item.label}
                              </span>
                              <span
                                className={cn(
                                  'text-xs font-semibold text-glass-900',
                                  item.status === 'exceeded' &&
                                  'text-red-500 drop-shadow-[0_0_6px_rgba(248,113,113,0.45)]',
                                  item.status === 'locked' && 'text-glass-400'
                                )}
                              >
                                {item.displayValue}
                              </span>
                            </div>
                          ))}
                          {monthlyOthersCount > 0 && (
                            <>
                              <button
                                type="button"
                                onClick={() => setShowMonthlyOthers((prev) => !prev)}
                                className="relative w-full px-1.5 py-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-glass-300 hover:text-glass-400 transition-colors"
                                aria-expanded={showMonthlyOthers}
                              >
                                <span className="absolute left-2 right-2 top-1/2 -translate-y-1/2 h-px bg-white/50" />
                                <span className="relative z-10 px-1">
                                  Diğer
                                </span>
                                <ChevronDown
                                  className={cn(
                                    'relative z-10 w-3 h-3 text-glass-300 transition-transform',
                                    showMonthlyOthers && 'rotate-180'
                                  )}
                                />
                              </button>

                              <div
                                className={cn(
                                  'grid grid-cols-1 gap-1 transition-all duration-200 overflow-hidden origin-top',
                                  showMonthlyOthers
                                    ? 'max-h-[520px] opacity-100 translate-y-0'
                                    : 'max-h-0 opacity-0 -translate-y-1 pointer-events-none'
                                )}
                              >
                                {monthlySecondary.map((item) => (
                                  <div
                                    key={item.key}
                                    className={cn(
                                      'rounded-lg border border-white/35 bg-white/50 px-2 py-1.5 flex items-center justify-between gap-2',
                                      item.status === 'locked' && 'opacity-70'
                                    )}
                                  >
                                    <span className="text-[11px] font-medium text-glass-800">
                                      {item.label}
                                    </span>
                                    <span
                                      className={cn(
                                        'text-[11px] font-semibold text-glass-900',
                                        item.status === 'exceeded' && 'text-red-500',
                                        item.status === 'locked' && 'text-glass-400'
                                      )}
                                    >
                                      {item.displayValue}
                                    </span>
                                  </div>
                                ))}

                                {totalLimits.map((item) => (
                                  <div
                                    key={item.key}
                                    className={cn(
                                      'rounded-lg border border-white/35 bg-white/50 px-2 py-1.5 flex items-center justify-between gap-2',
                                      item.status === 'locked' && 'opacity-70'
                                    )}
                                  >
                                    <span className="text-[11px] font-medium text-glass-800">
                                      {item.label}
                                    </span>
                                    <span
                                      className={cn(
                                        'text-[11px] font-semibold text-glass-900',
                                        item.status === 'exceeded' && 'text-red-500',
                                        item.status === 'locked' && 'text-glass-400'
                                      )}
                                    >
                                      {item.displayValue}
                                    </span>
                                  </div>
                                ))}

                                {sanayiMonthlyLimitItems.length > 0 && (
                                  <>
                                    <div className="pt-1">
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-glass-500 px-1">
                                        Sanayi Raporu
                                      </p>
                                    </div>
                                    {sanayiMonthlyLimitItems.map((item) => (
                                      <div
                                        key={item.key}
                                        className={cn(
                                          'rounded-lg border border-white/35 bg-white/50 px-2 py-1.5 flex items-center justify-between gap-2',
                                          item.status === 'locked' && 'opacity-70'
                                        )}
                                      >
                                        <span className="text-[11px] font-medium text-glass-800">
                                          {item.label}
                                        </span>
                                        <span
                                          className={cn(
                                            'text-[11px] font-semibold text-glass-900',
                                            item.status === 'exceeded' && 'text-red-500',
                                            item.status === 'locked' && 'text-glass-400'
                                          )}
                                        >
                                          {item.displayValue}
                                        </span>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Günlük Başlık */}
                        <div className="border-t border-white/40 pt-2 mt-1">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-glass-500 mb-1.5">
                            Günlük Limit
                          </p>
                        </div>

                        {/* Günlük Limitler */}
                        <div className="grid grid-cols-1 gap-1.5">
                          {dailyPrimary.map((item) => (
                            <div
                              key={item.key}
                              className={cn(
                                'rounded-xl border border-white/40 bg-white/60 px-2.5 py-2 shadow-inner backdrop-blur-sm flex items-center justify-between gap-2',
                                item.status === 'locked' && 'opacity-70'
                              )}
                            >
                              <span className="text-[12px] font-medium text-glass-900">
                                {item.key === 'chat' ? 'Çırak Mesajı' : item.label}
                              </span>
                              <span
                                className={cn(
                                  'text-xs font-semibold text-glass-900',
                                  item.status === 'exceeded' &&
                                  'text-red-500 drop-shadow-[0_0_6px_rgba(248,113,113,0.45)]',
                                  item.status === 'locked' && 'text-glass-400'
                                )}
                              >
                                {item.displayValue}
                              </span>
                            </div>
                          ))}
                          {dailyOthersCount > 0 && (
                            <>
                              <button
                                type="button"
                                onClick={() => setShowDailyOthers((prev) => !prev)}
                                className="relative w-full px-1.5 py-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-glass-300 hover:text-glass-400 transition-colors"
                                aria-expanded={showDailyOthers}
                              >
                                <span className="absolute left-2 right-2 top-1/2 -translate-y-1/2 h-px bg-white/50" />
                                <span className="relative z-10 px-1">
                                  Diğer
                                </span>
                                <ChevronDown
                                  className={cn(
                                    'relative z-10 w-3 h-3 text-glass-300 transition-transform',
                                    showDailyOthers && 'rotate-180'
                                  )}
                                />
                              </button>

                              <div
                                className={cn(
                                  'grid grid-cols-1 gap-1 transition-all duration-200 overflow-hidden origin-top',
                                  showDailyOthers
                                    ? 'max-h-[520px] opacity-100 translate-y-0'
                                    : 'max-h-0 opacity-0 -translate-y-1 pointer-events-none'
                                )}
                              >
                                {dailySecondary.map((item) => (
                                  <div
                                    key={item.key}
                                    className={cn(
                                      'rounded-lg border border-white/35 bg-white/50 px-2 py-1.5 flex items-center justify-between gap-2',
                                      item.status === 'locked' && 'opacity-70'
                                    )}
                                  >
                                    <span className="text-[11px] font-medium text-glass-800">
                                      {item.label}
                                    </span>
                                    <span
                                      className={cn(
                                        'text-[11px] font-semibold text-glass-900',
                                        item.status === 'exceeded' && 'text-red-500',
                                        item.status === 'locked' && 'text-glass-400'
                                      )}
                                    >
                                      {item.displayValue}
                                    </span>
                                  </div>
                                ))}

                                {totalLimits.map((item) => (
                                  <div
                                    key={item.key}
                                    className={cn(
                                      'rounded-lg border border-white/35 bg-white/50 px-2 py-1.5 flex items-center justify-between gap-2',
                                      item.status === 'locked' && 'opacity-70'
                                    )}
                                  >
                                    <span className="text-[11px] font-medium text-glass-800">
                                      {item.label}
                                    </span>
                                    <span
                                      className={cn(
                                        'text-[11px] font-semibold text-glass-900',
                                        item.status === 'exceeded' && 'text-red-500',
                                        item.status === 'locked' && 'text-glass-400'
                                      )}
                                    >
                                      {item.displayValue}
                                    </span>
                                  </div>
                                ))}

                                {sanayiLimitItems.length > 0 && (
                                  <>
                                    <div className="pt-1">
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-glass-500 px-1">
                                        Sanayi Raporu
                                      </p>
                                    </div>
                                    {sanayiLimitItems.map((item) => (
                                      <div
                                        key={item.key}
                                        className={cn(
                                          'rounded-lg border border-white/35 bg-white/50 px-2 py-1.5 flex items-center justify-between gap-2',
                                          item.status === 'locked' && 'opacity-70'
                                        )}
                                      >
                                        <span className="text-[11px] font-medium text-glass-800">
                                          {item.label}
                                        </span>
                                        <span
                                          className={cn(
                                            'text-[11px] font-semibold text-glass-900',
                                            item.status === 'exceeded' && 'text-red-500',
                                            item.status === 'locked' && 'text-glass-400'
                                          )}
                                        >
                                          {item.displayValue}
                                        </span>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Premium Action Buttons */}
                        <div className="flex flex-col gap-2 pt-2 border-t border-white/40">
                          {resolvedUserType === 'premium' &&
                            (premiumSource === 'subscription' || premiumSource === 'credit_purchase_bonus') && (
                            <DropdownMenu.Item asChild>
                              <button
                                onClick={handleManageSubscription}
                                disabled={portalLoading}
                                className="w-full rounded-lg border border-amber-300/60 bg-gradient-to-r from-amber-100 to-amber-200 px-3 py-2 text-[12px] font-semibold text-amber-800 shadow-sm transition-all duration-200 hover:from-amber-200 hover:to-amber-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                              >
                                {portalLoading ? (
                                  <>
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Yükleniyor...
                                  </>
                                ) : (
                                  <>
                                    {premiumSource === 'credit_purchase_bonus' ||
                                    managementSurface === 'app_store' ||
                                    managementSurface === 'play_store'
                                      ? 'ℹ️ Premium Bilgisi'
                                      : '⚙️ Aboneliğimi Yönet'}
                                  </>
                                )}
                              </button>
                            </DropdownMenu.Item>
                          )}
                        </div>

                        <div className="border-t border-white/30 pt-2 mt-2 flex flex-col gap-1.5">
                          <div className="flex items-center justify-between text-[10px] text-glass-500">
                            <span className="font-medium">
                              {limitsLoading ? 'Güncelleniyor...' : `Aylık ana kota, günlük fren ${resetCountdown}`}
                            </span>
                            {userId && (
                              <span className="text-[9px] text-gray-400/80">
                                hesap_no: {userId}
                              </span>
                            )}
                          </div>
                          {onSignOut && (
                            <div className="flex items-center justify-end gap-2">
                              {/* Destek Butonu - supportpage flag açıksa göster */}
                              {ozellikler.supportpage && (
                                <DropdownMenu.Item asChild>
                                  <button
                                    onClick={() => navigate('/destek')}
                                    className="rounded-md border border-blue-400/40 bg-blue-50/85 px-3 py-1.5 text-[11px] font-semibold text-blue-700 shadow-glass-sm transition-colors duration-200 hover:bg-blue-100"
                                  >
                                    💬 Destek
                                  </button>
                                </DropdownMenu.Item>
                              )}
                              <DropdownMenu.Item asChild>
                                <button
                                  onClick={onSignOut}
                                  className="rounded-md border border-white/40 bg-glass-900/90 px-3 py-1.5 text-[11px] font-semibold text-white shadow-glass-sm transition-colors duration-200 hover:bg-glass-900"
                                >
                                  Çıkış Yap
                                </button>
                              </DropdownMenu.Item>
                            </div>
                          )}
                        </div>
                      </div>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
            ) : onSignIn ? (
              <button
                onClick={onSignIn}
                className="relative group"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-accent-primary to-accent-secondary rounded-xl blur opacity-25 group-hover:opacity-50 transition-opacity" />
                <div className="relative px-4 py-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary text-white shadow-glass-sm hover:shadow-glass transition-all duration-200 active:scale-95 font-medium text-sm">
                  Giriş Yap
                </div>
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
